/**
 * De zinnen van de maandruimte-melding (Story 6.2, §10.2 van de PRD).
 *
 * Woord voor woord vastgesteld en hier niet bedacht of samengesteld (KE-7).
 * Alleen de maandnaam en de getallen worden ingevuld; de zinnen zelf
 * veranderen niet. Ontbreekt er een zin voor een geval, dan wordt §10 van de
 * PRD aangevuld — niet dit bestand.
 *
 * Ze staan in een eigen bestandje naast het formulier, in de vorm van
 * `correctieTermijnCopy.ts`, zodat de tekst op één plek leeft en niet door de
 * component heen verspreid raakt.
 */

import type { MaandruimteStand } from '@functieloonhuis/shared';
import { formatNlUren } from './urenFormat';

/** §10.2 — de toelichting bij de grens (besluit V3). */
export const MAANDRUIMTE_TOELICHTING_GRENS =
  'De 3 uur geldt per kalendermaand en gaat niet mee naar de volgende maand.';

/** §10.2 — de toelichting voor deeltijders (besluit V15). */
export const MAANDRUIMTE_TOELICHTING_DEELTIJD = 'Ook als je parttime werkt heb je 3 uur per maand.';

/** §10.2 — het eindpunt gaf geen stand. Een mededeling, nooit een fout (AC-8). */
export const MAANDRUIMTE_NIET_OP_TE_HALEN =
  'We konden je uren van deze maand even niet ophalen. Je kunt je aanvraag gewoon indienen.';

/**
 * §10.2 — welke van de vier zinnen er bij deze stand hoort.
 *
 * DE VOLGORDE IS BINDEND, en de eerste twee toetsen leunen op wat de SERVER
 * heeft vastgesteld — niet op een vergelijking hier (KE-5, AC-7):
 *
 * 1. `bovenDeGrens` — de server zegt dat deze aanvraag erboven uitkomt.
 * 2. deze aanvraag maakt de maand PRECIES vol.
 * 3. er was vóór deze aanvraag al niets meer over.
 * 4. er is nog ruimte.
 *
 * Waarom 2 vóór 3 staat: §10.2 schrijft die grens niet uit, en dit is de
 * leeskeuze van story 6.2 — *"is op"* geldt wanneer er vóóraf al niets meer
 * was, *"past precies"* wanneer deze aanvraag de maand precies vol maakt.
 */
export function maandruimteZin(stand: MaandruimteStand): string {
  const maximum = formatNlUren(stand.maximum);

  if (stand.bovenDeGrens === true) {
    const totaal = formatNlUren(stand.gebruiktInclusiefDezeAanvraag ?? stand.gebruikt);
    return (
      `Deze aanvraag brengt je op ${totaal} uur in ${stand.maand}, boven de ${maximum} uur. ` +
      'Je kunt hem gewoon indienen; HR beoordeelt hem daarna nog een keer.'
    );
  }

  if (
    stand.gebruiktInclusiefDezeAanvraag !== null &&
    stand.gebruiktInclusiefDezeAanvraag === stand.maximum
  ) {
    return `Dit past precies binnen je ${maximum} uur voor ${stand.maand}.`;
  }

  if (stand.resterend === 0) {
    return `Je ${maximum} uur voor ${stand.maand} is op.`;
  }

  return `Je hebt in ${stand.maand} nog ${formatNlUren(stand.resterend)} uur van je ${maximum} uur over.`;
}
